/**
 * Minimal typed fetch wrapper around the FastAPI backend.
 *
 * Resolves the base URL from `NEXT_PUBLIC_API_URL`, shapes errors, and
 * funnels every call through a single place so cross-cutting concerns
 * (auth header, request id, retry policy) land here later.
 *
 * Do NOT call `fetch` directly from feature code — always go through
 * `apiFetch`.
 */

import { clientEnv } from "@/lib/env";
import { redirectToLoginOnce } from "@/lib/login-redirect";

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export type ApiFetchOptions = RequestInit & {
  /** Request-id to forward; propagated by the reverse proxy in Phase 4. */
  requestId?: string;
};

export async function apiFetch<T>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  // When NEXT_PUBLIC_API_URL is empty the browser hits the API via the
  // reverse proxy on the page's own origin (Caddy routes /api/* → api:8000).
  // This keeps the client portable across hostnames (localhost, the VM
  // hostname, an SSH-forwarded port, etc.) since the env var is baked into
  // the bundle at build time.
  const base = clientEnv.NEXT_PUBLIC_API_URL;
  const url = base ? new URL(path, base).toString() : path;

  const headers = new Headers(options.headers);
  headers.set("Accept", "application/json");
  if (options.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (options.requestId) {
    headers.set("X-Request-Id", options.requestId);
  }

  const response = await fetch(url, {
    ...options,
    headers,
    credentials: "include",
  });

  const isJson = response.headers
    .get("content-type")
    ?.includes("application/json");
  const body: unknown = isJson
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    // The session is gone (C-100/C-103): send the user to sign in again
    // rather than leaving the screen full of "API 401" banners.
    if (response.status === 401) redirectToLoginOnce();
    throw new ApiError(
      response.status,
      `API ${response.status} on ${path}`,
      body,
    );
  }

  return body as T;
}
